import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router';

let currentPopGuard: ((event: PopStateEvent) => void) | undefined;
let installed = false;
let activeGuards = 0;

/**
 * A packing task guards navigation now (it asks before leaving unsaved work itself). Also on
 * `<html data-packing-guard>`, for parts of Hub that do not load this library (alert center).
 */
export const isPackingNavigationGuarded = () => activeGuards > 0;
const publishGuards = () => {
  if (activeGuards > 0) document.documentElement.dataset.packingGuard = String(activeGuards);
  else delete document.documentElement.dataset.packingGuard;
};
const dispatchPop = (event: PopStateEvent) => currentPopGuard?.(event);

/** Install before BrowserRouter: the temporary history traversal must not
 * notify the router and unmount a live task before its close guard runs. */
export function installPackingNavigationGuard() {
  if (installed || typeof window === 'undefined') return;
  window.addEventListener('popstate', dispatchPop, true);
  installed = true;
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    window.removeEventListener('popstate', dispatchPop, true);
    currentPopGuard = undefined;
    installed = false;
  });
}

/** Browser/Android Back bypasses navigator.go. Restore only indexed router entries. */
export function usePackingNavigationGuard(
  mayLeave: () => boolean,
  { enabled = true, onBack }: { enabled?: boolean; onBack?: () => void } = {},
) {
  const navigation = useContext(UNSAFE_NavigationContext)?.navigator;
  const check = useRef(mayLeave);
  check.current = mayLeave;
  const back = useRef(onBack);
  back.current = onBack;
  useEffect(() => {
    if (!navigation || !enabled) return;
    const { push, replace, go } = navigation;
    let index = window.history.state?.idx;
    let restoring: number | undefined;
    let afterRestore: (() => void) | undefined;
    navigation.push = (...args) => {
      if (check.current()) {
        push(...args);
        index = window.history.state?.idx;
      }
    };
    navigation.replace = (...args) => {
      if (check.current()) {
        replace(...args);
        index = window.history.state?.idx;
      }
    };
    navigation.go = (...args) => {
      if (typeof window.history.state?.idx === 'number' || check.current()) go(...args);
    };
    const pop = (event: PopStateEvent) => {
      const next = event.state?.idx;
      if (restoring !== undefined) {
        if (next === restoring) {
          event.stopImmediatePropagation();
          restoring = undefined;
          const action = afterRestore;
          afterRestore = undefined;
          action?.();
        } else if (typeof next === 'number') {
          // Repeated Back presses must not escape a restore in flight.
          event.stopImmediatePropagation();
          window.history.go(restoring - next);
        }
        return;
      }
      if (typeof index !== 'number' || typeof next !== 'number' || next === index) return;
      const closeTask = next < index ? back.current : undefined;
      if (closeTask || !check.current()) {
        event.stopImmediatePropagation();
        restoring = index;
        afterRestore = closeTask ? () => back.current?.() : undefined;
        window.history.go(index - next);
      } else index = next;
    };
    installPackingNavigationGuard();
    const previousPopGuard = currentPopGuard;
    currentPopGuard = pop;
    activeGuards += 1;
    publishGuards();
    return () => {
      activeGuards -= 1;
      publishGuards();
      navigation.push = push;
      navigation.replace = replace;
      navigation.go = go;
      if (currentPopGuard === pop) currentPopGuard = previousPopGuard;
    };
  }, [navigation, enabled]);
}
